import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button, H5, Card } from '../../lib/components'
import { SignNowEmbedFrame } from '../../lib/components/SignNowEmbedFrame'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { RootState } from '../../store'
import { documentServices, kycServices } from '../../services'
import { DocumentType } from '../../lib/types'
import './kyc.scss'

export type ClientAgreementServiceType = 'check1' | 'check2' | 'check3'

const SERVICE_OPTIONS: Array<{
  value: ClientAgreementServiceType
  label: string
  definition: string
}> = [
  {
    value: 'check1',
    label: 'Discretionary Portfolio Management Services',
    definition:
      "Credibila has full authority to make investment decisions (buy, sell, hold) on the Client's behalf, within the agreed strategy, without prior client approval.",
  },
  {
    value: 'check2',
    label: 'Non-Discretionary Portfolio Management Services',
    definition:
      "Credibila only advises and executes trades after the client's approval. All final decisions rest with the Client.",
  },
  {
    value: 'check3',
    label: 'Intermediation in the execution of securities transactions',
    definition:
      "Credibila acts as a broker to execute the Client's orders in the market, without providing advice or managing the portfolio.",
  },
]

interface ClientAgreementSigningProps {
  onSigned: () => void
}

const ClientAgreementSigning = ({ onSigned }: ClientAgreementSigningProps) => {
  const POLL_STATUS_INTERVAL = 3000
  const { authToken, userId } = useSelector((state: RootState) => state.investor)
  const [accessToken, setAccessToken] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [isButtonDisabled, setButtonDisable] = useState(true)
  const [buttonText, setButtonText] = useState('Loading..')
  const [isSentForSigning, setSentForSigning] = useState(false)
  const [signedDocumentLink, setSignedDocumentLink] = useState('')
  const [selectedService, setSelectedService] = useState<ClientAgreementServiceType | null>(null)
  const [signingLink, setSigningLink] = useState<string | null>(null)

  async function getDocumentAccessToken() {
    try {
      const response = await documentServices.getAccessToken({
        Authorization: authToken ? authToken : '',
      })

      if (response?.status === 200 && response?.data) {
        return response.data
      }
    } catch (error) {
      console.error('Error getting document access token:', error)
    }
    return null
  }

  async function getDocumentSigningLink(
    accessToken: string,
    documentType: DocumentType,
    clientAgreementServiceType?: ClientAgreementServiceType,
  ) {
    try {
      const response = await documentServices.getSigningLink(
        accessToken,
        documentType,
        { Authorization: authToken ? authToken : '' },
        undefined,
        clientAgreementServiceType,
      )

      if (response?.status === 200 && response?.data) {
        return response.data
      }
    } catch (error) {
      console.error('Error getting document signing link:', error)
    }
    return null
  }

  async function getDocumentDownloadLink(accessToken: string, documentId: string) {
    try {
      const response = await documentServices.getDownloadLink(accessToken, documentId, {
        Authorization: authToken ? authToken : '',
      })

      if (response?.status === 200 && response?.data) {
        return response.data
      }
    } catch (error) {
      console.error('Error getting document download link:', error)
    }
    return null
  }

  async function openClientAgreementSigning() {
    if (!selectedService) return
    setButtonDisable(true)
    setButtonText('Signing in progress..')

    // Mint a fresh link on every open — SignNow links expire and are effectively single-use
    const signingResponse = await getDocumentSigningLink(
      accessToken,
      'INVESTMENT_AGREEMENT',
      selectedService,
    )

    if (signingResponse?.signingLink && signingResponse?.documentId) {
      setDocumentId(signingResponse.documentId)
      setSigningLink(signingResponse.signingLink)
      setButtonText('Completing Client Agreement signing..')
      setSentForSigning(true)
      setSignedDocumentLink('polling')
      setButtonDisable(false)
    } else {
      setButtonText('Sign Client Agreement')
      setButtonDisable(false)
      setSigningLink(null)
      console.error('Failed to get signing link')
    }
  }

  async function handleClientAgreementBtnClick() {
    try {
      if (!isSentForSigning) {
        await openClientAgreementSigning()
      } else if (buttonText === 'Download Client Agreement') {
        setButtonText('Downloading Client Agreement..')
        setButtonDisable(true)
        const downloadLink = await getDocumentDownloadLink(accessToken, documentId)

        if (downloadLink) {
          window.open(downloadLink)
        }
        setButtonText('Download Client Agreement')
        setButtonDisable(false)
      } else if (signedDocumentLink === 'polling') {
        // Remint a fresh iframe link if the previous one expired
        await openClientAgreementSigning()
      }
    } catch (error) {
      console.error(error)
      setButtonDisable(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      const accessToken = await getDocumentAccessToken()
      if (accessToken) {
        setAccessToken(accessToken)
      }

      // Check if client agreement is already signed
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })

      if (response?.data?.client_agreement_signed) {
        setSentForSigning(true)
        setButtonText('Download Client Agreement')
        setSignedDocumentLink('signed')
        setSigningLink(null)
      } else {
        setButtonText('Sign Client Agreement')
        const savedService = response?.data?.client_agreement_service_type
        if (
          savedService &&
          (savedService === 'check1' || savedService === 'check2' || savedService === 'check3')
        ) {
          setSelectedService(savedService)
        }
      }

      setButtonDisable(false)
    })()
  }, [])

  const pollClientAgreementStatus = useCallback(async () => {
    try {
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })

      if (response?.data?.client_agreement_signed) {
        setSigningLink(null)
        setButtonText('Download Client Agreement')
        setSignedDocumentLink('signed')
        setButtonDisable(false)

        logCrmEvent(
          EngagementEvents.CLIENT_AGREEMENT_SIGNED,
          {
            Authorization: authToken ? authToken : '',
          },
          {
            userId: userId,
          },
        )

        onSigned()
        return true
      }
    } catch (error) {
      console.error('Error checking client agreement status:', error)
    }
    return false
  }, [authToken, onSigned, userId])

  // Close iframe when SignNow redirects to /invest/signing-complete; keep polling for signed
  const handleSigningComplete = useCallback(() => {
    setSigningLink(null)
    setButtonText('Completing Client Agreement signing..')
    void pollClientAgreementStatus()
  }, [pollClientAgreementStatus])

  // Poll KYC while awaiting confirmation (iframe open or post-redirect)
  useEffect(() => {
    if (!signedDocumentLink || signedDocumentLink === 'signed') return

    void pollClientAgreementStatus()

    const intervalID = setInterval(() => {
      void pollClientAgreementStatus().then((done) => {
        if (done) clearInterval(intervalID)
      })
    }, POLL_STATUS_INTERVAL)

    return () => clearInterval(intervalID)
  }, [signedDocumentLink, pollClientAgreementStatus, POLL_STATUS_INTERVAL])

  const isSigningOpen = Boolean(signingLink && signedDocumentLink === 'polling')

  return (
    <div
      className={`self-declaration-modal-container${
        isSigningOpen ? ' self-declaration-modal-container--signing' : ''
      }`}
    >
      <div>
        <Card
          classes={[
            'p-24 self-declaration-modal',
            isSigningOpen ? 'self-declaration-modal--signing' : '',
          ]}
          dataTestId='client-agreement-card'
        >
          <div className='self-declaration-wrapper self-declaration-wrapper-overflow mb-24'>
            <div className='self-declaration-wrapper-text'>
              <h1>CLIENT AGREEMENT & SELF-DECLARATION</h1>

              <div className='self-declaration-description mb-16'>
                <span className='basic'>
                  Please review and sign the Client Agreement and Self-Declaration before starting
                  KYC and to enable investing in opportunities.
                </span>
              </div>

              {!isSentForSigning && signedDocumentLink !== 'signed' && (
                <>
                  <H5 text='Services' classes={['mt-24', 'mb-12', 'fw-bold']} />
                  <p className='basic mb-20'>
                    Please select the service you would like to avail from the Firm.
                  </p>
                  <div className='client-agreement-service-options mb-28'>
                    {SERVICE_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className='client-agreement-service-option mb-16'
                        data-testid={`client-agreement-service-${option.value}`}
                      >
                        <label className='d-flex align-items-start gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={selectedService === option.value}
                            onChange={() => setSelectedService(option.value)}
                            data-testid={`client-agreement-checkbox-${option.value}`}
                            className='mt-1 mr-20'
                          />
                          <span className='client-agreement-service-label'>{option.label}</span>
                        </label>
                        <p className='basic text-muted small ms-4 mt-1 mb-0'>{option.definition}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {isSigningOpen && signingLink && (
                <div
                  className='client-agreement-signnow-embed'
                  data-testid='client-agreement-signnow-embed'
                >
                  <SignNowEmbedFrame
                    signingLink={signingLink}
                    title='Sign client agreement'
                    testId='client-agreement-signnow'
                    className='client-agreement-signnow-embed-frame'
                    minHeight={false}
                    onSigningComplete={handleSigningComplete}
                  />
                </div>
              )}

              <div className='self-declaration-btn-container'>
                <Button
                  text={isSigningOpen ? 'Get a new signing link' : buttonText}
                  handleClick={handleClientAgreementBtnClick}
                  isDisabled={
                    isButtonDisabled ||
                    (!isSentForSigning && signedDocumentLink !== 'signed' && !selectedService)
                  }
                  testId='client-agreement-btn'
                  variant='primary-btn'
                  classes={[
                    'btn',
                    'btn-primary',
                    'btn-lg',
                    'w-100',
                    isButtonDisabled ? 'btn-disabled' : '',
                  ]}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export { ClientAgreementSigning }
